import { BookingCarSelectionButton } from '@components/BookingFlow/Common/Car/BookingCarSelectionButton';
import { SALES_ITEM_BOOKING_FLOW_TYPE, SelectedSalesItemsCar } from 'noddi-types';
import { compareLicensePlates } from 'noddi-util';
import { useBookingActions, useBookingCars, useBookingSelectedSalesItemsCars } from 'noddi-web';

interface MultipleCarSelectionBlockProps {
  carsWithTirePickupOptions: SelectedSalesItemsCar[];
  salesItemId: number;
  isWheelPickupRequired: boolean;
  isAddonsAvailable: boolean;
  priceFormatted: string;
  mutuallyExclusiveSalesItemIds: number[];
}

export const MultipleCarSelectionBlock = ({
  carsWithTirePickupOptions,
  priceFormatted,
  salesItemId,
  isWheelPickupRequired,
  isAddonsAvailable,
  mutuallyExclusiveSalesItemIds
}: MultipleCarSelectionBlockProps) => {
  const cars = useBookingCars();
  const selectedSalesItemsCars = useBookingSelectedSalesItemsCars();

  const { removeSalesItems, addSalesItems } = useBookingActions();

  function onSelect({
    e,
    car,
    isAlreadySelected
  }: {
    e: React.MouseEvent<HTMLButtonElement>;
    car: SelectedSalesItemsCar;
    isAlreadySelected: boolean;
  }) {
    // Get the current tire hotel metadata from any selected car with this sales item
    const existingTireHotelMetadata = selectedSalesItemsCars.find(
      (item) =>
        item.salesItemId === salesItemId &&
        (item.metaData?.selectedTireHotelLocation || item.metaData?.selectedTireHotelPickUpAddress)
    )?.metaData;

    const params = [
      {
        licensePlate: car.licensePlate,
        salesItemId,
        type: SALES_ITEM_BOOKING_FLOW_TYPE.TIRE_HOTEL_PICKUP,
        mutuallyExclusiveSalesItemIds,
        isAddonsAvailable,
        isWheelPickupRequired,
        bookingCategoryType: car.bookingCategoryType,
        // Include the existing metadata so all cars have the same tire hotel location, we only support one location per booking anyways so this is fine to just copy it over
        ...(existingTireHotelMetadata && { metaData: existingTireHotelMetadata })
      }
    ];
    e.stopPropagation();

    isAlreadySelected ? removeSalesItems(params) : addSalesItems(params);
  }

  return (
    <div className='flex flex-col gap-2'>
      {carsWithTirePickupOptions.map((car) => {
        const isSelected = selectedSalesItemsCars.some(
          (item) => compareLicensePlates(item.licensePlate, car.licensePlate) && item.salesItemId === salesItemId
        );

        const carInfo = cars.find((item) => compareLicensePlates(item.licensePlate, car.licensePlate));
        return (
          <BookingCarSelectionButton
            key={car.licensePlate.number}
            car={carInfo}
            isSelected={isSelected}
            onClick={(event) => onSelect({ e: event, car, isAlreadySelected: isSelected })}
            licensePlateNumber={car.licensePlate.number}
            price={priceFormatted}
          />
        );
      })}
    </div>
  );
};
